/**
 * "From" picker of the composer (DESIGN.md T11, Outlook's From button / OWA "Show From"): the
 * user's own address and the shared mailboxes they may send as. The server checks the choice
 * again before sending. It is the shared EntityPicker over the sender identities, like every other
 * place that selects an entity.
 */
import { useMemo } from 'react';
import { EntityListPicker, type EntityOption } from '../composites/entity-picker';
import type { RecipientInputLabels } from './recipient-input';

export interface SenderIdentity {
  email: string;
  name: string | null;
  /** Short note shown under the address, e.g. "Shared mailbox". */
  note?: string | undefined;
}

export interface FromSelectProps {
  id?: string;
  identities: readonly SenderIdentity[];
  value: string;
  onChange: (email: string) => void;
  /** Accessible name of the field, e.g. "From". */
  label: string;
  /** Labels of the shared entity picker. */
  pickerLabels: RecipientInputLabels;
  disabled?: boolean;
}

export function FromSelect({ id, identities, value, onChange, label, pickerLabels, disabled }: FromSelectProps) {
  const items = useMemo<EntityOption<string>[]>(
    () => identities.map((i) => ({ id: i.email, label: i.name && i.name !== i.email ? i.name : i.email, identity: i.note ? `${i.email} · ${i.note}` : i.email })),
    [identities],
  );
  const current = identities.find((i) => i.email.toLowerCase() === value.toLowerCase()) ?? identities[0];
  if (!current) return null;
  return (
    <div className="min-w-0 flex-1">
      <EntityListPicker<string> {...(id ? { id } : {})} label={label} value={current.email} items={items} labels={pickerLabels} {...(disabled === undefined ? {} : { disabled })} onChange={(entry) => { if (entry) onChange(entry.id); }} />
    </div>
  );
}
